import React, { useMemo, lazy } from 'react';
import SuspenseWrapper from '@/components/SuspenseWrapper.tsx';
import ProgressBar from '@/ui/ProgressBar';
import { Users } from 'lucide-react';
const AfterFormSection = lazy(() => import('@/pages/Home/sections/afterFormSection'));

const TrustStarIcon = () => (
  <svg width="12" height="12" viewBox="0 0 42 42" fill="none" className="mt-[0.5px]">
    <path
      d="M21.0611 32.0283L30.2126 29.5849L34.0362 42L21.0611 32.0283ZM42.1221 15.9811H26.013L21.0611 0L16.1092 15.9811H0L13.0379 25.8868L8.08595 41.8679L21.1237 31.9622L29.1471 25.8868L42.1221 15.9811Z"
      fill="var(--color-accent)"
    />
  </svg>
);

export default function MainFormContainer({
  children,
  progress = 0,
  showReferral = false,
  aboveFooterContent,
  hideAfterForm = false,
  showAnimation = false,
  currentStep,
  totalSteps = 5,
  variant,
  footer,
  hideStepProgress = false,
  isLoading = false,
  isHome,
  contentClassName = '',
  backgroundColor,
  hideTimeLeft,
  hideTrustRail = false,
  hideProgressBar = false,
  innerContentClassName = '',
}: {
  children: React.ReactNode;
  progress?: number;
  showReferral?: boolean;
  aboveFooterContent?: React.ReactNode;
  hideAfterForm?: boolean;
  showAnimation?: boolean;
  currentStep?: number;
  totalSteps?: number;
  variant?: 'mobile-b';
  footer?: React.ReactNode;
  hideStepProgress?: boolean;
  isLoading?: boolean;
  isHome?: boolean;
  contentClassName?: string;
  backgroundColor?: string;
  hideTimeLeft?: boolean;
  hideTrustRail?: boolean;
  hideProgressBar?: boolean;
  innerContentClassName?: string;
}) {
  const determinedIsHome = useMemo(() => {
    if (isHome !== undefined) return isHome;
    return typeof window !== 'undefined' && window.location.pathname === '/';
  }, [isHome]);

  const afterFormContent = useMemo(() => {
    if (determinedIsHome) {
      return (
        <SuspenseWrapper fallbackClassName="h-0 w-full opacity-0">
          <AfterFormSection />
        </SuspenseWrapper>
      );
    }

    return null;
  }, [determinedIsHome]);

  if (variant === 'mobile-b') {
    return (
      <div
        className={`flex-1 flex flex-col w-full font-sans text-text-main ${contentClassName}`}
        style={{
          backgroundColor: backgroundColor || 'var(--color-primary-light)',
        }}
      >
        {/* Progress header - only shows if not hidden */}
        {!hideStepProgress && !hideProgressBar && (
          <div
            className="px-[18px] lg:px-0 pt-4 pb-0.5 w-full max-w-[800px] mx-auto"
            style={{ backgroundColor: backgroundColor || 'var(--color-primary-light)' }}
          >
            <div className="flex items-center gap-2.5">
              <div className="flex-1 h-1 bg-primary/10 rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary transition-transform duration-500 ease-out"
                  style={{
                    width: '100%',
                    transform: `scaleX(${(currentStep || 1) / (totalSteps || 6)})`,
                    transformOrigin: 'left',
                  }}
                />
              </div>
              <span className="text-body text-text-muted font-bold">
                Step {currentStep || 1}/{totalSteps || 6}
              </span>
            </div>

            {!hideTimeLeft && currentStep && currentStep >= 2 && currentStep <= 6 && (
              <div className="text-body text-primary font-bold">
                About {currentStep === 6 ? 10 : Math.max(10, 60 - (currentStep - 2) * 15)} seconds
                left
              </div>
            )}
          </div>
        )}
        {hideStepProgress && !hideProgressBar && (
          <div
            className="px-[18px] lg:px-0 pt-6 pb-2 w-full max-w-[800px] mx-auto"
            style={{ backgroundColor: backgroundColor || 'var(--color-primary-light)' }}
          >
            {progress > 0 ? (
              <div className="flex-1 h-1 bg-primary/10 rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary transition-all duration-300 ease-out"
                  style={{
                    width: `${progress}%`,
                  }}
                />
              </div>
            ) : (
              <div className="h-1 bg-primary rounded-full w-full" />
            )}
          </div>
        )}

        <div
          className={`flex-1 px-[18px] lg:px-0 pt-2 ${
            footer && !isLoading
              ? aboveFooterContent
                ? 'pb-[180px] md:pb-[220px]'
                : 'pb-[140px] md:pb-[180px]'
              : 'pb-10'
          } w-full max-w-[800px] mx-auto ${innerContentClassName}`}
        >
          {children}
        </div>

        {/* Fixed Footer with Trust Rail */}
        {footer && !isLoading && (
          <div className="fixed bottom-0 left-0 w-full bg-white border-t border-border-main z-[100]">
            {/* Instructional Content */}
            {aboveFooterContent && (
              <div className="w-full bg-primary-light px-[18px] py-[13.5px] border-b border-border-main">
                <div className="max-w-[800px] mx-auto">{aboveFooterContent}</div>
              </div>
            )}
            {!hideTrustRail && (
              <div className="max-w-[800px] mx-auto px-[18px] py-2.5 sm:py-4 flex items-center justify-between text-[clamp(9px,3vw,16px)] text-text-muted font-semibold border-b border-border-main">
                <span className="whitespace-nowrap">🔒 Secure</span>
                <span className="flex items-center gap-1 whitespace-nowrap">
                  <Users
                    size={12}
                    className="text-primary sm:w-[14px] sm:h-[14px]"
                    strokeWidth={3}
                  />{' '}
                  100,000+ helped
                </span>
                <span className="flex items-center gap-1 whitespace-nowrap">
                  <TrustStarIcon /> 4.5 Trustpilot
                </span>
              </div>
            )}
            {/* Button */}
            <div className="max-w-[800px] mx-auto p-4">{footer}</div>
          </div>
        )}

        {!footer && !isLoading && !hideTrustRail && (
          <div className="max-w-[800px] mx-auto px-[18px] py-2.5 sm:py-4 flex items-center justify-between text-[clamp(9px,3vw,16px)] text-text-muted font-semibold border-t border-border-main w-full">
            <span className="whitespace-nowrap">🔒 Secure</span>
            <span className="flex items-center gap-1 whitespace-nowrap">
              <Users size={12} className="text-primary sm:w-[14px] sm:h-[14px]" strokeWidth={3} />{' '}
              100,000+ helped
            </span>
            <span className="flex items-center gap-1 whitespace-nowrap">
              <TrustStarIcon /> 4.5 Trustpilot
            </span>
          </div>
        )}

        {!hideAfterForm && afterFormContent}
      </div>
    );
  }

  return (
    <div className="w-full flex-1 flex flex-col items-center bg-primary-light">
      <section
        className={`w-full max-w-[800px] flex flex-col ${
          !determinedIsHome ? 'md:my-10 md:p-10 md:bg-white md:rounded-3xl md:shadow-sm' : ''
        } ${contentClassName}`}
      >
        {!hideStepProgress && !hideProgressBar && (progress > 0 || currentStep !== undefined) && (
          <div className="px-5 pt-5">
            <ProgressBar
              progress={progress}
              showAnimation={showAnimation}
              currentStep={currentStep}
              totalSteps={totalSteps}
            />
          </div>
        )}
        {hideStepProgress && !hideProgressBar && (
          <div className="px-5 pt-5 pb-4">
            {progress > 0 ? (
              <div className="flex-1 h-1.5 bg-primary/10 rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary transition-all duration-300 ease-out"
                  style={{
                    width: `${progress}%`,
                  }}
                />
              </div>
            ) : (
              <div className="h-1.5 bg-primary rounded-full w-full" />
            )}
          </div>
        )}
        {children}
        {aboveFooterContent}

        {showReferral && <div className="">{/* <ReferrallInviteClaim /> */}</div>}
      </section>

      {!hideAfterForm && afterFormContent}
    </div>
  );
}
